import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

const SUPABASE_URL = "https://kenlllgwhwjnfxkynwem.supabase.co";
const SUPABASE_ANON_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImtlbmxsbGd3aHdqbmZ4a3lud2VtIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDg1NzQyODMsImV4cCI6MjA2NDE1MDI4M30.EhPoi5TsDG9emkOzpr9KdiOEHm7lFnZASqr8OwuAGIk";

export const useSessionTracking = () => {
  const { user } = useAuth();
  const location = useLocation();
  const sessionIdRef = useRef<string | null>(null);
  const sessionStartRef = useRef<Date | null>(null);
  const updateIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const pagesViewedRef = useRef<string[]>([]);

  // Create session on mount (authenticated users only)
  useEffect(() => {
    if (!user?.id) return;

    const createSession = async () => {
      try {
        const { data, error } = await supabase
          .from('session_tracking')
          .insert({
            user_id: user.id,
            session_start: new Date().toISOString(),
            pages_viewed: [location.pathname],
            events_triggered: 0,
            ip_address: null,
            user_agent: navigator.userAgent
          })
          .select()
          .single();

        if (error) {
          // Log but don't throw - session tracking is non-critical
          console.warn('[Session Tracking] Could not create session:', error.message);
          return; // Exit gracefully without blocking the app
        }

        sessionIdRef.current = data.id;
        sessionStartRef.current = new Date();
        pagesViewedRef.current = [location.pathname];

        console.log('[Session Tracking] Session created:', data.id);

        // Update session every 30 seconds
        updateIntervalRef.current = setInterval(() => {
          updateSessionActivity();
        }, 30000);

      } catch (error: any) {
        // Graceful error handling - session tracking should never block the app
        console.warn('[Session Tracking] Session creation failed:', error?.message || error);
        // Don't rethrow - let the app continue
      }
    };

    createSession();

    return () => {
      endSession();
      if (updateIntervalRef.current) {
        clearInterval(updateIntervalRef.current);
      }
    };
  }, [user?.id]);

  // Track page views on route change
  useEffect(() => {
    if (!sessionIdRef.current || !user?.id) return;

    const currentPath = location.pathname;
    
    if (!pagesViewedRef.current.includes(currentPath)) {
      pagesViewedRef.current.push(currentPath);
      updatePagesViewed();
    }
  }, [location.pathname, user?.id]);

  const updateSessionActivity = async () => {
    if (!sessionIdRef.current || !sessionStartRef.current) return;

    const durationSeconds = Math.floor((Date.now() - sessionStartRef.current.getTime()) / 1000);

    try {
      await supabase
        .from('session_tracking')
        .update({
          duration_seconds: durationSeconds,
          last_activity: new Date().toISOString()
        })
        .eq('id', sessionIdRef.current);

      console.log('[Session Tracking] Session updated, duration:', durationSeconds);
    } catch (error) {
      console.error('[Session Tracking] Error updating session:', error);
    }
  };

  const updatePagesViewed = async () => {
    if (!sessionIdRef.current) return;

    try {
      await supabase
        .from('session_tracking')
        .update({
          pages_viewed: pagesViewedRef.current
        })
        .eq('id', sessionIdRef.current);

      console.log('[Session Tracking] Pages viewed updated:', pagesViewedRef.current);
    } catch (error) {
      console.error('[Session Tracking] Error updating pages:', error);
    }
  };

  const endSession = async () => {
    if (!sessionIdRef.current || !sessionStartRef.current) return;

    const durationSeconds = Math.floor((Date.now() - sessionStartRef.current.getTime()) / 1000);

    try {
      await supabase
        .from('session_tracking')
        .update({
          session_end: new Date().toISOString(),
          duration_seconds: durationSeconds
        })
        .eq('id', sessionIdRef.current);

      console.log('[Session Tracking] Session ended:', sessionIdRef.current);
    } catch (error) {
      console.error('[Session Tracking] Error ending session:', error);
    }
  };

  const incrementEventsTriggered = async () => {
    if (!sessionIdRef.current) return;

    try {
      const { data } = await supabase
        .from('session_tracking')
        .select('events_triggered')
        .eq('id', sessionIdRef.current)
        .single();

      const currentCount = data?.events_triggered || 0;

      await supabase
        .from('session_tracking')
        .update({
          events_triggered: currentCount + 1
        })
        .eq('id', sessionIdRef.current);
    } catch (error) {
      console.error('[Session Tracking] Error incrementing events:', error);
    }
  };

  // Handle beforeunload to end session
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (sessionIdRef.current && sessionStartRef.current) {
        // Use fetch with keepalive for reliable session end on page unload
        const durationSeconds = Math.floor((Date.now() - sessionStartRef.current.getTime()) / 1000);
        
        fetch(`${SUPABASE_URL}/rest/v1/session_tracking?id=eq.${sessionIdRef.current}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'apikey': SUPABASE_ANON_KEY
          },
          body: JSON.stringify({
            session_end: new Date().toISOString(),
            duration_seconds: durationSeconds
          }),
          keepalive: true
        }).catch(err => console.error('[Session Tracking] Error on unload:', err));
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  return {
    sessionId: sessionIdRef.current,
    incrementEventsTriggered
  };
};
